import React from 'react';
import { Button, Typography, Empty } from 'antd';
import { Plus, BarChart3, LineChart, PieChart, Table2, TrendingUp } from 'lucide-react';

const { Text } = Typography;

// Design System styles
const styles = {
  primaryBackground: "#ffffff",
  secondaryBackground: "#DDE2EE",
  textPrimary: "#0f172a",
  textSecondary: "#666666",
  borderColor: "#E5E7EB",
  brandColor: "#1d0c46",
  headingFont: "Raleway, sans-serif",
  bodyFont: "Raleway, sans-serif"
};

// Visualization type icons mapping
const getVisualizationIcon = (type) => {
  switch (type) {
    case 'Line Chart':
      return <LineChart size={16} />;
    case 'Bar Chart':
      return <BarChart3 size={16} />;
    case 'Pie Chart':
      return <PieChart size={16} />;
    case 'KPI':
      return <TrendingUp size={16} />;
    case 'Table':
    default:
      return <Table2 size={16} />;
  }
};

const SavedQueriesSidebar = ({ 
  savedQueries = [], 
  onLoadQuery, 
  onRefresh,
  loading = false 
}) => {
  const handleLoadQuery = (query) => {
    onLoadQuery(query);
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div style={{ 
        overflowY: 'auto',
        overflowX: 'hidden',
        flex: 1,
        padding: '4px 0'
      }}>
        {savedQueries.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div style={{ textAlign: 'center' }}>
                <Text 
                  style={{ 
                    color: styles.textSecondary,
                    fontFamily: styles.bodyFont,
                    fontSize: '14px',
                    display: 'block',
                    marginBottom: '4px'
                  }}
                >
                  No saved queries yet
                </Text>
                <Text 
                  style={{ 
                    color: styles.textSecondary,
                    fontFamily: styles.bodyFont,
                    fontSize: '12px'
                  }}
                >
                  Save your first query to see it here
                </Text>
              </div>
            }
            style={{ 
              padding: '40px 20px',
              margin: 0
            }}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {savedQueries.map((query, index) => (
              <div
                key={query._id || query.id || index}
                onClick={() => handleLoadQuery(query)}
                style={{
                  padding: '12px 16px',
                  border: `1px solid ${styles.borderColor}`,
                  backgroundColor: styles.primaryBackground,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  borderRadius: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.secondaryBackground;
                  e.currentTarget.style.borderColor = styles.brandColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = styles.primaryBackground;
                  e.currentTarget.style.borderColor = styles.borderColor;
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      borderRadius: 0,
                      backgroundColor: styles.secondaryBackground,
                      color: styles.brandColor,
                      flexShrink: 0
                    }}
                  >
                    {getVisualizationIcon(query.visualizationType || 'Table')}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      strong
                      style={{ 
                        fontSize: '14px',
                        color: styles.textPrimary,
                        fontFamily: styles.headingFont,
                        fontWeight: 600,
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        lineHeight: '1.4'
                      }}
                    >
                      {query.name}
                    </Text>
                    {query.visualizationType && (
                      <Text
                        style={{ 
                          fontSize: '11px',
                          color: styles.brandColor,
                          fontFamily: styles.bodyFont,
                          fontWeight: 500,
                          display: 'block',
                          marginTop: '2px'
                        }}
                      >
                        {query.visualizationType}
                      </Text>
                    )}
                  </div>
                  <Button
                    type="primary"
                    size="small"
                    icon={<Plus size={14} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLoadQuery(query);
                    }}
                    style={{ 
                      backgroundColor: styles.brandColor,
                      borderColor: styles.brandColor,
                      borderRadius: 0,
                      fontFamily: styles.bodyFont,
                      fontWeight: 500,
                      fontSize: '12px',
                      height: '28px',
                      padding: '0 12px',
                      flexShrink: 0
                    }}
                  >
                    Add
                  </Button>
                </div>

                {query.description && (
                  <Text
                    style={{ 
                      fontSize: '12px',
                      color: styles.textSecondary,
                      fontFamily: styles.bodyFont,
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      lineHeight: '1.4',
                      marginTop: '2px'
                    }}
                  >
                    {query.description}
                  </Text>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedQueriesSidebar;
